import React from 'react'
import memoizee from 'memoizee'
import {
  validateCreateMissionDto,
  maybeGetSuggestionText,
  convertCreateMissionDtoToMission,
  getDateFromStrings,
} from './../../RosterView/util'
import { createLowerCaseHashMapFromArray } from './../../../../Utils/Array'
import {
  toLocaleNonAmericanDateString,
  getWeekNumber,
  browserSafeNewDate,
  dropTimeZoneFromDate,
  get24HourTime,
  getShortWeekDay,
  formatToLocalDate,
  instantiateLocalDate,
} from './../../../../shared/libraries/utils/date'
import { downloadStringAsFile } from './../../../../Utils/Misc'
import {
  isDollars,
  // isDollarsAdjustment,
  isNumber,
} from '../../../../Utils/Validators/csv'
import {
  MISSION_ENUM_TYPES,
  MISSION_STATUS_ENUM,
  COUNTRY_CODES,
  CAMPAIGN_ENUM_TYPES,
} from '../../../../shared/constants'
import {
  getDisplayedWeekNumber,
  numberInStringSort,
  getWeeksFilterOptions,
  defaultMissionFilters,
  getOptions,
} from '../../../../Utils/Table'
import { calculateTimeOfDay } from '../../../../Utils/Mission'
import { normalizeGraphQLConnection } from '../../../../shared/graphql/utils'
import { StatusLabel } from '../../../../Components/StatusLabel'
import { numberFormatterWithOptional2Dp } from 'Utils/Formatters'
import { MISSION_NO_SHOWN_STATUS } from '../../../../constants'
import {
  isNeedToHideEmployeeLastName,
  hideUserLastName,
} from '../../../../Utils/User'

//VALIDATE CLIENT_DISTRIBUTION_COST and BA_PAYOUT
function getErrorForNumber(input) {
  if (input == null || input === '' || input === undefined) {
    // This checks for both null and undefined (and an empty string)
    return `Value (${input}) - invalid: Please provide a numeric value.`
  }

  // Check for spaces in the input
  if (/\s/.test(input)) {
    return `Value (${input}) - space in between numbers detected, please remove all spaces in between numbers from your file.`
  }

  // Check for non-numeric characters except for dot and comma
  if (/[^0-9.,]/.test(input)) {
    return `Value (${input}) - not a number: Text detected, please put a number value.`
  }

  // Check for commas in the input
  if (/,/.test(input)) {
    return `Value (${input}) - comma detected, please remove all commas from your file.`
  }

  // Validate the number format
  const regex = /^\d+(\.\d+)?$/
  if (!regex.test(input)) {
    return `Value ${input} - invalid format: Please ensure the number is in the correct format (e.g., 1000, 1000.00).`
  }

  return null
}

const formatToFixedNumber = (number, options = 2) =>
  number ? Number(Number(number).toFixed(options)) : 0

const createHash = memoizee(createLowerCaseHashMapFromArray)

export const convertMissionDtoToMission = function(
  dto,
  { locations, ambassadors, cityPlans, type },
) {
  var ambassadorsHash = {}
  ambassadors.forEach(
    a =>
      (ambassadorsHash[
        a.firstName.toLowerCase() + ' ' + a.lastName.toLowerCase()
      ] = a.id),
  )
  var cityPlansHash = {}

  cityPlans.forEach(c => {
    cityPlansHash[c.city.name.toLowerCase()] = c.id
    cityPlansHash[c.city.id] = c.id
  })

  var data = {
    ...convertCreateMissionDtoToMission(dto, { locations }),
    userId: ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()],
    cityPlanId: cityPlansHash[dto['CITY_NAME'].toLowerCase()],
    checkInTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKIN_TIME'])),
      cityPlans[0].city.timezone,
    ),
    checkoutTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKOUT_TIME'])),
      cityPlans[0].city.timezone,
    ),
    flyerCode: dto['FLYER_CODE'],
    tag: dto['TAG'],
    distributionCost: Number(dto['DISTRIBUTION_COST']),
    surgePayout: Number(dto['PAYOUT']),
    printingCostPerFlyer: Number(dto['PRINTING_COST_PER_FLYER']),
    checkoutFlyerQuantity: Number(dto['CHECKOUT_FLYER_QUANTITY']),
    // These missions should all be historic
    status: MISSION_STATUS_ENUM.COMPLETED,
    type,
    // LB:
    boundary: [{ latitude: 0, longitude: 0 }],
    boundaryColour: dto['BOUNDARY_COLOR'],
  }

  if (dto['BONUS_ON_CONVERSION']) {
    data.bonusOnConversion = dto['BONUS_ON_CONVERSION']
  }

  if (dto['PAY_HOURLY']) {
    data.payHourly = convertBoolean(dto['PAY_HOURLY'])
  }

  if (dto['PAYOUT_RATIO']) {
    data.payoutRatio = dto['PAYOUT_RATIO']
  }

  return data
}

export const validateMissionDto = (
  dto,
  { locations, cities, ambassadorNames },
) => {
  // Do the same validation as when creating a new mission
  validateCreateMissionDto(dto, { locations })

  if (!dto['CITY_NAME']) throw new Error("Missing the 'CITY_NAME' column.")

  if (!dto['AMBASSADOR_NAME'])
    throw new Error("Missing the 'AMBASSADOR_NAME' column.")

  if (!dto['FLYER_CODE']) throw new Error('Missing flyer code.')

  if (!dto['DISTRIBUTION_COST']) throw new Error('Missing distribution cost.')

  if (isDollars(dto['DISTRIBUTION_COST']) === false)
    throw new Error(
      'Distribution cost is not valid, expects a dollar amount with cents as two decimal places. e.g. 104.00',
    )

  // Is reduced by the surge ratio if checkin time isn't within the surge time
  if (!dto['PAYOUT']) throw new Error('Missing payout.')

  if (
    dto['PAYOUT'] !== undefined &&
    dto['PAYOUT'] !== null &&
    isDollars(dto['PAYOUT']) === false
  )
    throw new Error(
      'Payout is not valid, expects a dollar amount with cents as two decimal places. e.g. 57.00',
    )

  //if (!dto['CHECKIN_DATE']) throw new Error('Missing checkin date.')

  if (!dto['CHECKIN_TIME']) throw new Error('Missing checkin time.')

  //if (!dto['CHECKOUT_DATE']) throw new Error('Missing checkout date.')

  if (!dto['CHECKOUT_TIME']) throw new Error('Missing checkout time.')

  if (!dto['CHECKOUT_FLYER_QUANTITY'])
    throw new Error('Missing checkout flyer quantity.')

  if (!dto['PRINTING_COST_PER_FLYER'])
    throw new Error('Missing printing cost per flyer.')

  // TODO Validate the PRINTING_COST_PER_FLYER as numeric
  if (isNumber(dto['PRINTING_COST_PER_FLYER']) === false)
    throw new Error('Printing cost per flyer is invalid.')

  // bonus on conversion is optional
  if (dto['BONUS_ON_CONVERSION'] !== undefined) {
    if (isDollars(dto['BONUS_ON_CONVERSION']) === false)
      throw new Error(
        'Bonus on conversion is not valid, expects a dollar amount with cents as two decimal places. e.g. 1.25',
      )
  }

  if (dto['PAYOUT_RATIO'] !== undefined)
    if (isNumber(dto['PAYOUT_RATIO']) === false)
      throw new Error('Payout ratio is invalid.')

  let cityNamesHash = {}

  cities.forEach(c => {
    cityNamesHash[c.name.toLowerCase()] = c.id
    cityNamesHash[String(c.id)] = c.id
  })
  const cityId =
    dto['CITY_NAME'] && dto['CITY_NAME'].toLowerCase
      ? dto['CITY_NAME'].toLowerCase()
      : dto['CITY_NAME']

  // eslint-disable-next-line no-console
  console.log({
    cityNamesHash,
    value: dto['CITY_NAME'],
    cityId,
    locations,
  })

  if (!cityNamesHash[dto['CITY_NAME'].toLowerCase()]) {
    var error = new Error(
      `City name ${dto['CITY_NAME']} does not match existing 
      city plans ${Object.keys(cityNamesHash).join(', ')}.`.replace(
        /\s+/g,
        ' ',
      ),
    )
    error.suggestion = maybeGetSuggestionText(
      dto['CITY_NAME'],
      Object.keys(cityNamesHash),
    )
    throw error
  }

  var ambassadorsHash = createHash(ambassadorNames)

  if (!ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()]) {
    var error = new Error(
      `Could not find ambassador ${dto['AMBASSADOR_NAME']}.`,
    )
    error.suggestion = maybeGetSuggestionText(
      dto['AMBASSADOR_NAME'],
      ambassadorNames,
    )
    throw error
  }
}

export const downloadSampleCSV = function({
  locationNames,
  cityNames,
  ambassadorNames,
  isNeedToAddTagColumn = false,
  campaignType,
}) {
  var lastWeek = new Date()
  lastWeek.setDate(lastWeek.getDate() - 7)
  var exampleDate = toLocaleNonAmericanDateString(lastWeek)
  // Grab a random location
  if (locationNames.length) {
    var exampleIndex = Math.floor(Math.random() * locationNames.length)
    var exampleLocation = locationNames[exampleIndex]
  } else {
    var exampleLocation = 'Central Station'
  }

  if (cityNames.length) {
    var exampleIndex = Math.floor(Math.random() * cityNames.length)
    var exampleCityName = cityNames[exampleIndex]
  } else {
    var exampleCityName = 'Paris'
  }

  if (ambassadorNames.length) {
    var exampleIndex = Math.floor(Math.random() * ambassadorNames.length)
    var exampleAmbassadorName = ambassadorNames[exampleIndex]
  } else {
    var exampleAmbassadorName = 'Joe Blogs'
  }

  const data = {
    'DATE': exampleDate,
    'START_TIME': '08:00',
    'LOCATION': exampleLocation,
    'CITY_NAME': exampleCityName,
    'AMBASSADOR_NAME': exampleAmbassadorName,
    'CHECKIN_TIME': '08:00',
    'CHECKOUT_TIME': '11:00',
    'FLYER_CODE': 'EXAMPLECODEABC',
    'TAG': '2x15€',
    ...(CAMPAIGN_ENUM_TYPES.DIRECTMAIL === campaignType && {
      'ARTWORK_CODE': '4234',
    }),
    'CHECKOUT_FLYER_QUANTITY': '',
    'CLIENT_DISTRIBUTION_COST': '', // 'DISTRIBUTION_COST'
    'BA_PAYOUT': '', //   'PAYOUT'
    'CLIENT_PRINT_COST_PER_FLYER': '', //   'PRINTING_COST_PER_FLYER'
  }
  if (!isNeedToAddTagColumn) {
    delete data.TAG
  }

  downloadStringAsFile(
    Object.keys(data).join(',') + '\n' + Object.values(data).join(','),
    `historic-data-example.csv`,
  )
}

/**
 * Converts a user string falsey/truthy value
 * @param {string} str
 * @returns {Boolean}
 */
export const convertBoolean = function(str) {
  switch (str.toLowerCase().trim()) {
    case 'no':
    case 'false':
    case '':
      return false
    default:
      return true
  }
}

/**
 * Calculate the bonus cost for a mission, computed from redux state props.
 * @param {string} mission
 * @returns {number}
 */
export const calculateBonusCostForGQLMission = mission => {
  // Filter out those conversions which won't be paid
  if (mission.bonusPerConversion && mission.bonusRedemptionPeriod) {
    const cutOff = new Date(mission.dateTime)
    cutOff.setDate(cutOff.getDate() + mission.bonusRedemptionPeriod)
    const conversions = mission.conversions.nodes.filter(c => {
      return c.metric.isPrimary && c.timestamp < cutOff.toISOString()
    })
    return (mission.bonusPerConversion * conversions.length) / 100
  } else {
    return 0
  }
}

/**
 * Calculates permit cost for each mission based on:
 * If campaign has permit cost set
 * If mission needs permit
 * If that week contains any missions that need a permit
 *
 * @param {number|string} missionId
 * @param {HashMap} hashMaps.missions - The mission that permit cost will be calculated for
 * @param {HashMap} hashMaps.cityPlan - City plan in order to
 * calculate how much missions needs permit cost in this roster
 * @param {HashMap} hashMaps.permitCosts - All permit costs
 * @param {HashMap} hashMaps.missions - HashMap of missions in order to get mission object
 * when looping over city plan missions to count missions that need permit cost
 * @param {HashMap} hashMaps.locations - HashMap of locations in order to get mission's location
 *
 * @return {Number} - Permit cost for the mission
 *
 * @example
 *
 * const hashMaps = {
 *   cityPlans: {
 *     2: {
 *       permitCostIds:[ "1111", "2222", ],
 *       missionIds:[ "1111", "2222", ]
 *     },
 *   locations: {
 *     32: {
 *       hasPermit: true,
 *     },
 *   },
 *   missions: {
 *     1111: {
 *       id: 1,
 *       location: 44,
 *       dateTime: '2019-07-21T10:00:00+10:00',
 *     },
 *   },
 *   permitCosts: {
 *     1111: {
 *       startDate: '2019-07-21T10:00:00+10:00',
 *       cost: 44,
 *     },
 *   }
 * }
 *
 * calculatePermitCostForMission(1, hashMaps)
 *
 */
export const calculatePermitCostForGQLMission = ({
  mission,
  cityPlan,
  campaignMissions,
}) => {
  if (
    cityPlan.permitCosts.nodes.length === 0 ||
    mission.type === MISSION_ENUM_TYPES.LETTERBOX
  ) {
    return 0
  }

  const missionDateTimeObj = new Date(mission.dateTime)
  const missionWeekNumber = getWeekNumber(missionDateTimeObj)

  const permitCostsByWeekMap = cityPlan.permitCosts.nodes.reduce(
    (accMap, costItem) => {
      const dateObj = new Date(costItem.startDate)
      const weekNumber = getWeekNumber(dateObj)
      accMap.set(weekNumber, {
        permitCost: costItem.cost,
      })
      return accMap
    },
    new Map(),
  )

  if (
    !permitCostsByWeekMap.get(missionWeekNumber) ||
    permitCostsByWeekMap.get(missionWeekNumber).permitCost === 0
  ) {
    return 0
  }

  const missionsByWeekMap = campaignMissions.reduce((accMap, missionItem) => {
    const dateObj = new Date(missionItem.dateTime)
    const weekNumber = getWeekNumber(dateObj)
    const prevRecord = accMap.get(weekNumber)
    if (!prevRecord) {
      accMap.set(weekNumber, {
        permitCost: 0,
        missions: [missionItem],
      })
    } else {
      const prevMissions = prevRecord.missions ? prevRecord.missions : []
      accMap.set(weekNumber, {
        ...prevRecord,
        missions: [...prevMissions, missionItem],
      })
    }

    return accMap
  }, permitCostsByWeekMap)

  const {
    permitCost: weekPermitCostCents,
    missions: weekMissions,
  } = missionsByWeekMap.get(missionWeekNumber)

  const weekPermitCost = weekPermitCostCents / 100
  const weekMissionsAmount = weekMissions.length
  const { location } = mission

  const amountOfMissionsWithPermit = weekMissions.filter(mission => {
    return location.hasPermit
  }).length

  if (amountOfMissionsWithPermit === 0) {
    return weekPermitCost / weekMissionsAmount
  } else if (location.hasPermit && weekPermitCost > 0) {
    return weekPermitCost / amountOfMissionsWithPermit
  }

  return 0
}

/**
 * Gets all the data required to display on the master schedule, it also includes
 * additional information that will be shown when the data is exported.
 * @returns {Object}
 */
export const getMasterScheduleMissionExportData = ({
  mission,
  city,
  campaign,
  campaignMissions,
  cityPlan,
  zipCodesHashMap,
}) => {
  const {
    location,
    user: ba,
    metricResults,
    // type,
    label,
    spyInformation,
  } = mission

  const locationName = label ? label : location ? location.name : '-'

  let baInfo = '-'
  if (ba) {
    baInfo = `${ba.firstName} ${
      isNeedToHideEmployeeLastName()
        ? hideUserLastName(ba.lastName)
        : ba.lastName
    }`
  }

  let spyInfo = '-'
  if (spyInformation && spyInformation.spy) {
    spyInfo = `${spyInformation.spy.firstName} ${
      isNeedToHideEmployeeLastName()
        ? hideUserLastName(spyInformation.spy.lastName)
        : spyInformation.spy.lastName
    }`
  }

  // Get a date relative to the missions timezone
  const localDate = browserSafeNewDate(dropTimeZoneFromDate(mission.dateTime))

  const checkInTime = mission.checkInTime
    ? get24HourTime(
        browserSafeNewDate(dropTimeZoneFromDate(mission.checkInTime)),
      )
    : '-'

  const checkoutTime = mission.checkoutTime
    ? get24HourTime(
        browserSafeNewDate(dropTimeZoneFromDate(mission.checkoutTime)),
      )
    : '-'

  var data = {
    // Needed for links
    id: mission.id,
    'City': city.name,
    'Week': getDisplayedWeekNumber(localDate),
    'Date': toLocaleNonAmericanDateString(localDate),
    'Time': get24HourTime(localDate),
    'Time of day': calculateTimeOfDay(
      mission.dateTime,
      city.lunchCheckInTime,
      city.afternoonCheckInTime,
    ),
    'Day of week': getShortWeekDay(localDate.getDay()),
    'Location': locationName,
    'BA': baInfo,
    'Spy': spyInfo,
    'Code': mission.flyerCodeV2 ? mission.flyerCodeV2.code : '-',
    'Tag': mission.flyerCodeV2 ? mission.flyerCodeV2.tag : '-',
    'Flyers distributed': mission.totalFlyersDistributed || '-',
    'Status': (() => {
      if (mission.status == 'UNDER_REVIEW') return 'UNDER REVIEW'
      return mission.status
    })(),
    // Extra columns for exporting the data
    'Location Type': mission.location ? mission.location.type.name : '-',
    'Pack Size': mission.packSize,
    'Distribution Cost': mission.distributionCost,
    'Printing Cost': formatToFixedNumber(
      mission.totalFlyersDistributed * (mission.printingCostPerFlyerV2 / 100),
    ),
    'Cost Per Flyer': mission.printingCostPerFlyerV2
      ? mission.printingCostPerFlyerV2 / 100
      : '-',
    'Zip Code': zipCodesHashMap[mission.id] || '-',
  }

  data['Cash Bonus'] = calculateBonusCostForGQLMission(mission)

  // Figure out the permit cost
  data['Permit Cost'] = formatToFixedNumber(
    calculatePermitCostForGQLMission({
      mission,
      cityPlan,
      campaignMissions,
    }),
  )

  data['Hard Cost'] = formatToFixedNumber(
    formatToFixedNumber(data['Distribution Cost']) +
      formatToFixedNumber(data['Printing Cost']) +
      formatToFixedNumber(data['Cash Bonus']) +
      formatToFixedNumber(data['Permit Cost']),
  )

  data['Scans'] = mission.amountScans

  metricResults.nodes.forEach(result => {
    const { metric, value } = result
    const campaignMetric = campaign.metrics.nodes.find(
      ({ id }) => id === metric.id,
    )
    let campaignMetricName = campaignMetric.label || campaignMetric.name
    if (data[campaignMetricName] !== undefined) {
      campaignMetricName = `${campaignMetricName} 2`
    }
    data[campaignMetricName] = value
    data[`${campaignMetricName} CVR`] = value
      ? formatToFixedNumber(value / mission.totalFlyersDistributed, 4)
      : 0
    data[`Cost per ${campaignMetricName}`] =
      value == 0 ? '-' : formatToFixedNumber(data['Hard Cost'] / value)
  })

  // Link to the mission
  var { protocol, hostname, port } = window.location
  data['Link'] = `${protocol}//${hostname}${
    port ? ':' + port : ''
  }/dashboard/mission/${mission.id}`

  data['Check In Time'] = checkInTime
  data['Checkout Time'] = checkoutTime
  data['BA Payout'] = mission.surgePayout

  return data
}

/**
 * Gets all the data required to display on the master schedule
 * @returns {Object}
 */
export const getMasterScheduleMissionData = ({
  currencySign,
  mission,
  campaign,
  isClient,
  // campaignMissions,
}) => {
  const {
    location,
    user: ba,
    metricResults,
    // , type
    label,
  } = mission
  // Get a date relative to the missions timezone
  const localDate = browserSafeNewDate(dropTimeZoneFromDate(mission.dateTime))

  const locationName = label ? label : location ? location.name : '-'

  //TODO WHY?
  // const locationDependingOnTypeMission =
  //   type === MISSION_ENUM_TYPES.LETTERBOX ? label : locationName

  let baInfo = '-'
  if (ba) {
    baInfo = `${ba.firstName} ${
      isNeedToHideEmployeeLastName()
        ? hideUserLastName(ba.lastName)
        : ba.lastName
    }`
  }

  let spyInfo = '-'
  if (mission.spyInformation && mission.spyInformation.spy) {
    spyInfo = `${mission.spyInformation.spy.firstName} ${
      isNeedToHideEmployeeLastName()
        ? hideUserLastName(mission.spyInformation.spy.lastName)
        : mission.spyInformation.spy.lastName
    }`
  }

  var data = {
    'City': mission.cityPlan.city.name,
    'Week': getDisplayedWeekNumber(localDate),
    'Date': toLocaleNonAmericanDateString(localDate),
    'Time': get24HourTime(localDate),
    'Time of day': mission.timeOfDay,
    'Day of week': getShortWeekDay(localDate.getDay()),
    'Spy': spyInfo,
    'Location': convertLabelToZipCode({
      label: locationName,
      zipCode: mission.zipCode,
    }),
    'BA': baInfo,
    'Code': mission.flyerCode || '-',
    'Tag': mission.flyerCodeV2 ? mission.flyerCodeV2.tag : '-',
    'Flyers distributed': mission.totalFlyersDistributed || '-',
    'Status': (() => {
      let { status } = mission
      if (mission.status == 'UNDER_REVIEW') status = 'UNDER REVIEW'
      if (mission.cancelled && mission.payoutRatio > 0)
        status = 'CANCELLED_PAID'
      if (mission.cancelled && mission.payoutRatio == 0) status = 'CANCELLED'
      return (
        <div>
          <StatusLabel
            isSpied={
              mission.spyInformation &&
              mission.spyInformation.spyDetails &&
              mission.spyInformation.spyDetails.checkInTime
            }
            status={status}
          />
          {mission.isNotShown && !isClient && (
            <StatusLabel status={MISSION_NO_SHOWN_STATUS} />
          )}
        </div>
      )
    })(),
  }
  if (campaign.scanDataEnabled) {
    data['Scans'] = mission.amountScans
  }

  const printingCost =
    mission.totalFlyersDistributed * mission.printingCostPerFlyer

  const distributionCost = mission.distributionCost
  const bonusCost = calculateBonusCostForGQLMission(mission)
  const hardCost = printingCost + distributionCost + bonusCost

  metricResults.nodes.forEach(result => {
    const { metric, value } = result

    let campaignMetricName = metric.label ? metric.label : metric.name
    if (data[campaignMetricName] !== undefined) {
      campaignMetricName = `${campaignMetricName} 2`
    }
    data[campaignMetricName] = value
    data[`Cost per ${campaignMetricName}`] =
      value == 0 ? '-' : currencySign + Number(hardCost / value).toFixed(2)
    data[`${campaignMetricName} CVR`] =
      value && mission.totalFlyersDistributed
        ? numberFormatterWithOptional2Dp(
            (value / mission.totalFlyersDistributed) * 100,
          ) + '%'
        : 0
  })

  return Object.values(data)
}

export const formatData = (data, campaign, formatter, isClient) => {
  const missions = normalizeGraphQLConnection(data.campaign.missions)

  return {
    data: missions.map(mission =>
      formatter({
        currencySign: data.campaign.country
          ? data.campaign.country.currencySign
          : '$',
        mission,
        campaign,
        campaignMissions: missions,
        isClient,
      }),
    ),
    actions: missions.map(mission => () => {
      window.open(`/dashboard/mission/${mission.id}`, '_blank')
    }),
    baRedirectActions: missions.map(mission => () => {
      window.open(
        `/dashboard/cities/${mission.cityPlan.city.id}/ambassadors/${
          mission.user.id
        }`,
        '_blank',
      )
    }),
    totalCount: data.campaign.missions.totalCount,
    cityPlans: data.campaign.cityPlans.nodes.map(cityPlan => {
      const edges = cityPlan.city.locations.edges.reduce((acc, { node }) => {
        if (!campaign.isSelfServe && !node.isSelfServe) {
          acc.push({ node })
        }

        if (campaign.isSelfServe) {
          if (!node.campaignId || node.campaignId === campaign.id) {
            acc.push({ node })
          }
        }

        return acc
      }, [])

      return {
        id: cityPlan.id,
        city: { ...cityPlan.city, locations: { edges } },
      }
    }),
    missions,
  }
}

export const formatDataExport = (
  cityPlans,
  campaign,
  formatter,
  zipCodesHashMap,
) => ({
  cityPlans,
  missions: cityPlans.reduce((acc, cityPlan) => {
    const { city, missions } = cityPlan
    const flattenMissions = normalizeGraphQLConnection(missions)
    return [
      ...acc,
      ...flattenMissions.map(mission =>
        formatter({
          mission,
          city,
          campaign,
          campaignMissions: flattenMissions,
          cityPlan,
          zipCodesHashMap,
        }),
      ),
    ]
  }, []),
})

export const getFilters = ({ cities = [], locations = [] } = {}) => [
  {
    label: 'City',
    options: getOptions(cities),
  },
  {
    label: 'Location',
    options: getOptions(locations, numberInStringSort),
    sorted: false,
  },
  {
    label: 'Week',
    options: getWeeksFilterOptions(),
    sorted: false,
  },
  ...defaultMissionFilters,
]

export const convertMissionDtoToMissionMasterSchedule = function(
  values,
  { locations, ambassadors, cityPlans, type },
) {
  const dto = formatDtoMasterSchedule(values)
  let ambassadorsHash = {}
  ambassadors.forEach(
    a =>
      (ambassadorsHash[
        a.firstName.toLowerCase() + ' ' + a.lastName.toLowerCase()
      ] = a.id),
  )
  let cityPlansHash = {}

  cityPlans.forEach(c => {
    cityPlansHash[c.city.name.toLowerCase()] = c.id
    cityPlansHash[c.city.id] = c.id
  })

  const data = {
    ...convertCreateMissionDtoToMission(dto, { locations }),
    userId: ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()],
    cityPlanId: cityPlansHash[dto['CITY_NAME'].toLowerCase()],
    checkInTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKIN_TIME'])),
      cityPlans[0].city.timezone,
    ),
    checkoutTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKOUT_TIME'])),
      cityPlans[0].city.timezone,
    ),
    flyerCode: dto['FLYER_CODE'].toUpperCase(),
    // tag: dto['TAG'],
    distributionCost: Number(dto['CLIENT_DISTRIBUTION_COST']),
    surgePayout: Number(dto['BA_PAYOUT']),
    printingCostPerFlyer: Number(dto['CLIENT_PRINT_COST_PER_FLYER']),
    checkoutFlyerQuantity: Number(dto['CHECKOUT_FLYER_QUANTITY']),
    // These missions should all be historic
    status: MISSION_STATUS_ENUM.COMPLETED,
    type,
    // LB:
    boundary: [{ latitude: 0, longitude: 0 }],
    boundaryColour: dto['BOUNDARY_COLOR'],
  }

  if (dto['BONUS_ON_CONVERSION']) {
    data.bonusOnConversion = dto['BONUS_ON_CONVERSION']
  }

  if (dto['PAY_HOURLY']) {
    data.payHourly = convertBoolean(dto['PAY_HOURLY'])
  }

  if (dto['PAYOUT_RATIO']) {
    data.payoutRatio = dto['PAYOUT_RATIO']
  }

  if (dto['TAG']) {
    data.tag = dto['TAG']
  }

  if (dto['ARTWORK_CODE']) {
    data.artworkId = dto['ARTWORK_CODE']
  }

  return data
}

const formatDtoMasterSchedule = data => {
  const CONVERT_EXPORT_HEADERS_TO_IMPORT = {
    'Date': 'DATE',
    'Time': 'START_TIME',
    'Location': 'LOCATION',
    'City': 'CITY_NAME',
    'BA': 'AMBASSADOR_NAME',
    'Check In Time': 'CHECKIN_TIME',
    'Checkout Time': 'CHECKOUT_TIME',
    'Code': 'FLYER_CODE',
    'Artwork Code': 'ARTWORK_CODE',
    'Flyers distributed': 'CHECKOUT_FLYER_QUANTITY',
    'Distribution Cost': 'CLIENT_DISTRIBUTION_COST', // 'DISTRIBUTION_COST'
    'BA Payout': 'BA_PAYOUT', //   'PAYOUT'
    'Printing Cost': 'CLIENT_PRINT_COST_PER_FLYER', //   'PRINTING_COST_PER_FLYER'
  }

  if (Object.keys(data).includes('City')) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (CONVERT_EXPORT_HEADERS_TO_IMPORT[key]) {
        acc[CONVERT_EXPORT_HEADERS_TO_IMPORT[key]] = value
      }

      return acc
    }, {})
  } else {
    return data
  }
}

const validateLocation = ({ dto, locations, campaignType }) => {
  if (campaignType !== CAMPAIGN_ENUM_TYPES.DIRECTMAIL) {
    return
  }
  const importedLocationName = dto['LOCATION']
  const importedCityName = dto['CITY_NAME']
  const existingLocation = locations.find(
    l => l.name === importedLocationName && l.cityName === importedCityName,
  )

  if (
    existingLocation &&
    existingLocation.type &&
    existingLocation.type.name !== 'Direct Mail'
  ) {
    throw new Error(
      `Location ${
        existingLocation.name
      } in city ${importedCityName} is not a Direct Mail location.`,
    )
  }
}

export const validateMissionDtoMasterSchedule = (
  data,
  { locations, cities, ambassadorNames, campaignType, artworksIds },
) => {
  const dto = formatDtoMasterSchedule(data)

  validateLocation({ dto, locations, campaignType })

  validateCreateMissionDto(dto, { locations, campaignType })

  if (!dto['CITY_NAME']) throw new Error("Missing the 'CITY_NAME' column.")

  if (!dto['AMBASSADOR_NAME'])
    throw new Error("Missing the 'AMBASSADOR_NAME' column.")

  if (!dto['FLYER_CODE']) throw new Error('Missing the FLYER_CODE column.')

  if (campaignType === CAMPAIGN_ENUM_TYPES.DIRECTMAIL && !dto['ARTWORK_CODE'])
    throw new Error('Missing the ARTWORK_CODE column.')

  if (
    campaignType === CAMPAIGN_ENUM_TYPES.DIRECTMAIL &&
    !artworksIds.includes(dto['ARTWORK_CODE'])
  )
    throw new Error('ARTWORK_CODE does not match any artwork in the campaign.')

  if (!dto['CLIENT_DISTRIBUTION_COST'])
    throw new Error('Missing the CLIENT_DISTRIBUTION_COST column.')

  const errorClientDistributionCost = getErrorForNumber(
    dto['CLIENT_DISTRIBUTION_COST'],
  )
  if (errorClientDistributionCost) throw new Error(errorClientDistributionCost)

  // Is reduced by the surge ratio if checkin time isn't within the surge time
  if (!dto['BA_PAYOUT']) throw new Error('Missing the BA_PAYOUT column.')

  const errorBaPayout = getErrorForNumber(dto['BA_PAYOUT'])
  if (errorBaPayout) throw new Error(errorBaPayout)

  //if (!dto['CHECKIN_DATE']) throw new Error('Missing checkin date.')

  if (!dto['CHECKIN_TIME']) throw new Error('Missing the CHECKIN_TIME column.')

  //if (!dto['CHECKOUT_DATE']) throw new Error('Missing checkout date.')

  if (!dto['CHECKOUT_TIME'])
    throw new Error('Missing the CHECKOUT_TIME column.')

  if (!dto['CHECKOUT_FLYER_QUANTITY'])
    throw new Error('Missing the CHECKOUT_FLYER_QUANTITY column.')

  if (!dto['CHECKOUT_FLYER_QUANTITY'])
    throw new Error('Missing the CHECKOUT_FLYER_QUANTITY column.')

  if (!Number.isInteger(Number(dto['CHECKOUT_FLYER_QUANTITY'])))
    throw new Error('CHECKOUT_FLYER_QUANTITY must be integer.')

  if (!dto['CLIENT_PRINT_COST_PER_FLYER'])
    throw new Error('Missing the CLIENT_PRINT_COST_PER_FLYER column.')

  const errorClientPrintCost = getErrorForNumber(
    dto['CLIENT_PRINT_COST_PER_FLYER'],
  )
  if (errorClientPrintCost) throw new Error(errorClientPrintCost)

  // bonus on conversion is optional
  if (dto['BONUS_ON_CONVERSION'] !== undefined) {
    if (isDollars(dto['BONUS_ON_CONVERSION']) === false)
      throw new Error(
        'BONUS_ON_CONVERSION is not valid, expects a dollar amount with cents as two decimal places. e.g. 1.25',
      )
  }

  if (dto['PAYOUT_RATIO'] !== undefined)
    if (isNumber(dto['PAYOUT_RATIO']) === false)
      throw new Error('PAYOUT_RATIO is invalid.')

  let cityNamesHash = {}

  cities.forEach(c => {
    cityNamesHash[c.name.toLowerCase()] = c.id
    cityNamesHash[String(c.id)] = c.id
  })
  const cityId =
    dto['CITY_NAME'] && dto['CITY_NAME'].toLowerCase
      ? dto['CITY_NAME'].toLowerCase()
      : dto['CITY_NAME']

  // eslint-disable-next-line no-console
  console.log({
    cityNamesHash,
    value: dto['CITY_NAME'],
    cityId,
    locations,
  })

  if (!cityNamesHash[dto['CITY_NAME'].toLowerCase()]) {
    var error = new Error(
      `CITY_NAME ${dto['CITY_NAME']} does not match existing 
      city plans ${Object.keys(cityNamesHash).join(', ')}.`.replace(
        /\s+/g,
        ' ',
      ),
    )
    error.suggestion = maybeGetSuggestionText(
      dto['CITY_NAME'],
      Object.keys(cityNamesHash),
    )
    throw error
  }

  var ambassadorsHash = createHash(ambassadorNames)

  if (!ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()]) {
    var error = new Error(
      `Could not find ambassador ${dto['AMBASSADOR_NAME']}.`,
    )
    error.suggestion = maybeGetSuggestionText(
      dto['AMBASSADOR_NAME'],
      ambassadorNames,
    )
    throw error
  }
}

export const calculateCentroid = coords => {
  let centroid = { latitude: 0, longitude: 0 }
  let totalVertices = coords.length

  // Sum all latitude and longitude coordinates
  coords.forEach(coord => {
    centroid.latitude += coord.latitude
    centroid.longitude += coord.longitude
  })

  // Divide by the number of vertices to get the average
  centroid.latitude /= totalVertices
  centroid.longitude /= totalVertices

  return centroid
}

export const formatDataToFetchPostcodes = data => {
  const cityPlans = data.cityPlans.nodes

  const countryCode = cityPlans[0].city.country.code

  const result = []

  for (const cityPlan of cityPlans) {
    const {
      missions: { edges },
    } = cityPlan

    for (const item of edges) {
      const { node: mission } = item

      if (
        [MISSION_ENUM_TYPES.HANDTOHAND, MISSION_ENUM_TYPES.DIRECTMAIL].includes(
          mission.type,
        )
      ) {
        const { latitude, longitude } = mission.location

        result.push({ latitude, longitude, id: mission.id })
      }

      if (MISSION_ENUM_TYPES.LETTERBOX === mission.type) {
        const { latitude, longitude } = calculateCentroid(mission.boundary)

        result.push({ latitude, longitude, id: mission.id })
      }
    }
  }

  return { countryCode, data: result }
}

export const convertZipCodesToHashMap = zipCodes =>
  zipCodes.reduce((acc, item) => {
    acc[item.id] = item.zipCode
    return acc
  }, {})

export const getStatusesExportedMissions = ({
  isClient,
  isExportAllMissions,
}) => {
  const ALL_MISSIONS_STATUSES = [
    MISSION_STATUS_ENUM.DRAFT,
    MISSION_STATUS_ENUM.AVAILABLE,
    MISSION_STATUS_ENUM.BOOKED,
    MISSION_STATUS_ENUM.CANCELLED,
    MISSION_STATUS_ENUM.CHECKED_IN,
    MISSION_STATUS_ENUM.UNDER_REVIEW,
    MISSION_STATUS_ENUM.COMPLETED,
    MISSION_STATUS_ENUM.PAID,
  ]

  const CHECKOUT_MISSIONS_STATUSES = [
    MISSION_STATUS_ENUM.UNDER_REVIEW,
    MISSION_STATUS_ENUM.COMPLETED,
    MISSION_STATUS_ENUM.PAID,
    MISSION_STATUS_ENUM.CANCELLED,
  ]

  const filterStatusesForClient = statuses =>
    statuses.filter(status => status !== MISSION_STATUS_ENUM.CANCELLED)

  if (isClient) {
    if (isExportAllMissions) {
      return filterStatusesForClient(ALL_MISSIONS_STATUSES)
    } else {
      return filterStatusesForClient(CHECKOUT_MISSIONS_STATUSES)
    }
  } else {
    if (isExportAllMissions) {
      return ALL_MISSIONS_STATUSES
    } else {
      return CHECKOUT_MISSIONS_STATUSES
    }
  }
}

export const convertLabelToZipCode = ({ label, zipCode }) => {
  if (!zipCode) {
    return label
  }

  if (label.includes('Unnamed_area')) {
    return zipCode
  }

  return label
}

export const getSortableHeaders = ({ countryCode, campaignType, headers }) => {
  if (
    [COUNTRY_CODES.AUSTRALIA, COUNTRY_CODES.NEW_ZEALAND].includes(
      countryCode,
    ) &&
    campaignType === CAMPAIGN_ENUM_TYPES.LETTERBOX
  ) {
    return headers.filter(header => header !== 'Location')
  }
  return headers
}
